import { html, css, unsafeCSS, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import SotbitInput from '../index.js';
import { DEFAULT_INPUT_PROPERTIES } from '@/components/input/constants.js';
import { EVENT_MASK_COMPLETE, EVENT_MASK_ACCEPT } from '@/components/input/masked/constants.js';
import { ATTRIBUTES, SESSION_STORAGE_KEY } from '@/components/input/phone/constants.js';
import styles from './style.scss?inline';
import COUNTRIES_DATA from '@/data/countries.json';
import { createInputMask } from '@/directives/InputMaskDirective.js';
import { map } from 'lit/directives/map.js';

export default class SotbitInputPhone extends SotbitInput {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        ...DEFAULT_INPUT_PROPERTIES,
        _country: { type: String, state: true },
        _showCode: { type: Boolean, state: true },
        /** Mask pattern in placeholder only while the text field is focused (inactive field stays visually empty). */
        _inputFocused: { type: Boolean, state: true },
        noCountryChange: { type: Boolean, attribute: ATTRIBUTES.NO_COUNTRY_CHANGE },
        defaultCountry: { type: String, attribute: ATTRIBUTES.DEFAULT_COUNTRY },
    };

    // country that is used if default-country attribute is not set
    #FALLBACK_COUNTRY = 'RU';

    constructor() {
        super();

        this._showCode = this.value !== '';
        this._inputFocused = false;
        this._country = this.getSavedCountry();
    }

    get value() {
        let value = '';

        if (this._mask?.value) {
            value = `${this.getCurrentCountryPhoneCode()} ${this._mask.value}`;
        } else {
            value = this.inputRef.value?.value || '';
        }

        return value;
    }

    set value(value) {
        if (!(this.inputRef.value instanceof HTMLInputElement)) {
            return;
        }

        value = value.trim().replace(/[^0-9+]/g, '');

        if (this._mask) {
            this._mask.unmaskedValue = value.replace(this.getCurrentCountryPhoneCode(), '');
        } else {
            this.inputRef.value.value = value;
        }

        this._internals?.setFormValue(this.value);

        if (this.inputRef?.value) {
            this._validationController.validate();
        }

        if (this.error) {
            this.checkValidity(false);
        }

        if (this.value) {
            this.focus({ preventScroll: true }); // show phone code if input is not empty
        }
    }

    getCurrentCountryPhoneCode() {
        return COUNTRIES_DATA[this._country]?.code || '';
    }

    getCurrentCountryMask() {
        return COUNTRIES_DATA[this._country]?.mask || '';
    }

    getDefaultCountry() {
        return this.defaultCountry || this.#FALLBACK_COUNTRY;
    }

    getSavedCountry() {
        if (!this._storageId) {
            this._initializeStorage();
        }

        return window.sessionStorage.getItem(this._storageId);
    }

    saveCurrentCountry() {
        if (!this._storageId) {
            this._initializeStorage();
        }

        window.sessionStorage.setItem(this._storageId, this._country);
    }

    _initializeStorage() {
        this._storageId = this._generateStorageId();
    }

    _generateStorageId() {
        return SESSION_STORAGE_KEY;
    }

    connectedCallback() {
        super.connectedCallback();

        // in case "default-country" attribute is not set
        if (!this._country) {
            this._country = this.#FALLBACK_COUNTRY;
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this._mask?.destroy();
    }

    firstUpdated() {
        super.firstUpdated();

        if (!this.inputRef.value) {
            return;
        }

        this._mask = createInputMask(this.inputRef.value, {
            mask: this.getCurrentCountryMask(),
            lazy: true,
        });

        this._mask.on('accept', () => {
            this._internals?.setFormValue(this.value);
            this._validationController.validate();
            this.dispatchEvent(
                new Event(EVENT_MASK_ACCEPT, {
                    bubbles: true,
                    composed: true,
                })
            );
        });

        this._mask.on('complete', () => {
            this.dispatchEvent(
                new Event(EVENT_MASK_COMPLETE, {
                    bubbles: true,
                    composed: true,
                })
            );
        });

        queueMicrotask(async () => {
            if (this.inputRef.value === document.activeElement) {
                this._inputFocused = true;
                await this.updateComplete;
                this._mask?.updateOptions({
                    mask: this.getCurrentCountryMask(),
                    lazy: true,
                });
            }
        });
    }

    async _onPhoneInputFocus() {
        this._inputFocused = true;
        await this.updateComplete;
        this._mask?.updateOptions({
            mask: this.getCurrentCountryMask(),
            lazy: true,
        });
    }

    async _onPhoneInputBlur() {
        this._inputFocused = false;
        await this.updateComplete;
        this._mask?.updateOptions({
            mask: this.getCurrentCountryMask(),
            lazy: true,
        });
    }

    _handleFocusIn(e) {
        const input = this.inputRef.value;
        if (!(input instanceof HTMLInputElement) || e.target !== input) {
            return;
        }

        this._showCode = true;
    }

    /** Keep focus out of the anchor so IMask / label state tied to the text input is not triggered. */
    _preventCountryAnchorTakingFocus(e) {
        e.preventDefault();
    }

    /** Do not bubble to sotbit-input-style .input-field (it always focuses the text input on click). */
    _stopCountryDropdownBubble(e) {
        e.stopPropagation();
    }

    _handleFocusOut() {
        this._showCode = this.value !== '';
    }

    _handleCountrySelect({ detail }) {
        let country = detail.value;

        if (!COUNTRIES_DATA[country]) {
            country = this.getDefaultCountry();
        }

        this._country = country;

        this.saveCurrentCountry();

        this._mask?.updateOptions({
            mask: this.getCurrentCountryMask(),
        });
    }

    _renderDropdown() {
        if (this.noCountryChange) {
            return nothing;
        }

        return html`
            <sotbit-dropdown
                slot="icon-right"
                is-select
                items-right
                use-teleport
                @click="${this._stopCountryDropdownBubble}"
                @mousedown="${this._stopCountryDropdownBubble}"
                @change="${this._handleCountrySelect}"
            >
                <button
                    slot="anchor"
                    part="change-country"
                    type="button"
                    @mousedown="${this._preventCountryAnchorTakingFocus}"
                >
                    <sotbit-flag country="${this._country}"></sotbit-flag>
                </button>

                ${map(Object.entries(COUNTRIES_DATA), ([countryCode, { name, code }]) => {
                    // backslash is used in mask pattern to escape zero (0) character, we strip it for the list view
                    const phoneCode = code.replaceAll('\\', '');

                    return html`<sotbit-dropdown-item value="${countryCode}" ?active="${this._country === countryCode}">
                        <sotbit-flag slot="icon-left" country="${countryCode}"></sotbit-flag>
                        <span slot="value">${name} ${phoneCode}</span>
                    </sotbit-dropdown-item>`;
                })}
            </sotbit-dropdown>
        `;
    }

    render() {
        return html`
            <sotbit-input-style
                size="${this.noCountryChange ? 'm' : 'l'}"
                error-text="${this.errorText || nothing}"
                helper-text="${this.helperText || nothing}"
                label="${this.noCountryChange ? nothing : this.label}"
                part="input-style"
                ?error="${this.error}"
                @focusin="${this._handleFocusIn}"
                @focusout="${this._handleFocusOut}"
                ${ref(this.styleRef)}
            >
                <span slot="input" part="code" ?hidden="${!this._showCode}">${this.getCurrentCountryPhoneCode()}</span>
                <input
                    type="text"
                    name="${this.name || nothing}"
                    slot="input"
                    part="input"
                    placeholder="${this._inputFocused ? this.getCurrentCountryMask() : ''}"
                    ?disabled="${this.disabled}"
                    ?readonly="${this.readonly}"
                    ?required="${this.required}"
                    @focus="${this._onPhoneInputFocus}"
                    @blur="${this._onPhoneInputBlur}"
                    @input="${this._handleInput}"
                    @keydown="${this._handleKeydown}"
                    ${ref(this.inputRef)}
                />
                ${this._renderDropdown()}
            </sotbit-input-style>
        `;
    }
}
